import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';
import { DayOfWeek } from '@vendasta/meetings';
import moment, { Moment } from 'moment';
import { TimeRange } from '../../../interface';

export interface EditAvailabilityDialogData {
  day: DayOfWeek;
  ranges: TimeRange[];
  saveCallback: (changes: Map<DayOfWeek, TimeRange[]>) => void;
  isMultiHost: boolean;
  availableSlots: string[];
}

// We allow 15 minute intervals.
const allowedAvailabilityIntervals = [0, 15, 30, 45, 60];

@Component({
  selector: 'meeting-scheduler-edit-availability-dialog',
  templateUrl: 'edit-availability-dialog.component.html',
  styleUrls: ['edit-availability-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditAvailabilityDialogComponent implements OnInit {
  starts: UntypedFormArray;
  startsErrors: UntypedFormArray;
  ends: UntypedFormArray;
  endsErrors: UntypedFormArray;
  form: UntypedFormGroup;
  copyToWeekdays = false;

  // Expose DayOfWeek enum to template
  readonly DayOfWeek = DayOfWeek;

  // Weekdays constant to avoid duplication
  private readonly WEEKDAYS: DayOfWeek[] = [
    DayOfWeek.MONDAY,
    DayOfWeek.TUESDAY,
    DayOfWeek.WEDNESDAY,
    DayOfWeek.THURSDAY,
    DayOfWeek.FRIDAY,
  ];

  constructor(
    public dialogRef: MatDialogRef<EditAvailabilityDialogComponent>,
    private readonly translate: TranslateService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly cd: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) readonly data: EditAvailabilityDialogData,
  ) {}

  ngOnInit(): void {
    this.starts = this.formBuilder.array(this.data.ranges.map((range) => this.dateToTime(range.start)));
    this.ends = this.formBuilder.array(this.data.ranges.map((range) => this.dateToTime(range.end)));
    this.startsErrors = this.formBuilder.array(this.data.ranges.map(() => false));
    this.endsErrors = this.formBuilder.array(this.data.ranges.map(() => false));
    this.form = this.formBuilder.group({
      starts: this.starts,
      ends: this.ends,
      startsErrors: this.startsErrors,
      endsErrors: this.endsErrors,
    });
    this.form.setValidators([
      this.validateIndividualRanges(),
      this.noIntersections(),
      this.noDuplicates(),
      this.noVaTimePickerErrors(),
    ]);
  }

  changeRangeStart(index: number, time: string): void {
    const starts = this.starts.value;
    if (index >= 0 && starts.length > index) {
      starts[index] = time;
      this.starts.setValue(starts);
    }
  }

  changeRangeEnd(index: number, $event: MatSelectChange): void {
    const ends = this.ends.value;
    if (index >= 0 && ends.length > index) {
      ends[index] = $event.value;
      this.ends.setValue(ends);
    }
  }

  removeRange(index: number): void {
    if (index >= 0 && this.starts.length > index && this.ends.length > index) {
      this.starts.removeAt(index);
      this.ends.removeAt(index);
      this.startsErrors.removeAt(index);
      this.endsErrors.removeAt(index);
    }
  }

  addRange(): void {
    this.starts.push(this.formBuilder.control(''));
    this.ends.push(this.formBuilder.control(''));
    this.startsErrors.push(this.formBuilder.control(false));
    this.endsErrors.push(this.formBuilder.control(false));
  }

  close(): void {
    this.dialogRef.close();
  }

  callbackAndClose(): void {
    const starts = this.starts.value;
    const ends = this.ends.value;

    // Build ranges using map for better performance and readability
    const ranges: TimeRange[] = starts.map((start: string, index: number) => ({
      start: this.timeToMoment(start).toDate(),
      end: this.timeToMoment(ends[index]).toDate(),
    }));

    const changes = new Map<DayOfWeek, TimeRange[]>();

    // Always save the current day
    changes.set(this.data.day, ranges);

    if (this.copyToWeekdays) {
      // Also copy current day's schedule to all weekdays
      this.WEEKDAYS.forEach((day) => {
        changes.set(day, [...ranges]);
      });
    }

    this.data.saveCallback(changes);
    this.close();
  }

  private isWeekday(day: DayOfWeek): boolean {
    return this.WEEKDAYS.includes(day);
  }

  validateIndividualRanges(): ValidatorFn {
    return (): { [key: string]: any } => {
      if (!this.starts || !this.ends) {
        return null;
      }
      const starts = this.starts.value;
      const ends = this.ends.value;
      for (let i = 0; i < starts.length; i++) {
        const start = starts[i];
        const end = ends[i];
        const startMoment = this.timeToMoment(start);
        const endMoment = this.timeToMoment(end);
        if (!startMoment.isValid() || !endMoment.isValid()) {
          return { noEmptyTimes: {} };
        }
        if (allowedAvailabilityIntervals.indexOf(startMoment.minutes()) < 0) {
          return { noDisallowedTimes: {} };
        }
        if (allowedAvailabilityIntervals.indexOf(endMoment.minutes()) < 0) {
          return { noDisallowedTimes: {} };
        }
        if (endMoment.diff(startMoment) < 0) {
          return { noStartsAfterEnds: {} };
        }
        if (endMoment.diff(startMoment) === 0) {
          return { noZeroDurationRanges: {} };
        }
      }
      return null;
    };
  }

  noIntersections(): ValidatorFn {
    return (): { [key: string]: any } => {
      if (!this.starts || !this.ends) {
        return null;
      }
      const starts = this.starts.value;
      const ends = this.ends.value;
      const times = starts.concat(ends);
      for (const time of times) {
        const timeMoment = this.timeToMoment(time);
        for (let i = 0; i < starts.length; i++) {
          const startMoment = this.timeToMoment(starts[i]);
          const endMoment = this.timeToMoment(ends[i]);
          if (timeMoment.isBetween(startMoment, endMoment)) {
            return { noIntersections: {} };
          }
        }
      }
      return null;
    };
  }

  noDuplicates(): ValidatorFn {
    return (): { [key: string]: any } => {
      if (!this.starts || !this.ends) {
        return null;
      }
      const starts = this.starts.value;
      const ends = this.ends.value;
      const ranges: string[] = [];
      if (starts.length < 2) {
        return null;
      }
      for (let i = 0; i < starts.length; i++) {
        const startFormatted = this.timeToMoment(starts[i]).format('HH:mm');
        const endFormatted = this.timeToMoment(ends[i]).format('HH:mm');
        ranges.push(startFormatted + '-' + endFormatted);
      }
      const matches = ranges.reduce((prev, curr) => {
        return prev === curr ? 't' : '';
      });
      if (matches.length > 0) {
        return { noDuplicates: {} };
      }
      return null;
    };
  }

  noVaTimePickerErrors(): ValidatorFn {
    return (): { [key: string]: any } => {
      if (!this.startsErrors || !this.endsErrors) {
        return null;
      }
      const startsWithErrors = this.startsErrors.value.filter((errors) => errors);
      const endsWithErrors = this.endsErrors.value.filter((errors) => errors);
      if (startsWithErrors.length > 0 || endsWithErrors.length > 0) {
        return { vaTimePickerErrors: {} };
      }
      return null;
    };
  }

  getValidationError(): string {
    const errors = this.form.errors || {};
    if (errors.noEmptyTimes) {
      // no messaging as they just need to make a selection
      return '';
    }
    if (errors.vaTimePickerErrors) {
      // the time pickers already show their own internal errors
      return '';
    }
    if (errors.noDisallowedTimes) {
      return this.translate.instant('MEETING_SCHEDULER.SCHEDULE_SETTINGS.AVAILABILITY.EDIT_NO_DISALLOWED_TIMES');
    }
    if (errors.noStartsAfterEnds) {
      return this.translate.instant('MEETING_SCHEDULER.SCHEDULE_SETTINGS.AVAILABILITY.EDIT_NO_STARTS_AFTER_ENDS');
    }
    if (errors.noZeroDurationRanges) {
      return this.translate.instant('MEETING_SCHEDULER.SCHEDULE_SETTINGS.AVAILABILITY.EDIT_NO_ZERO_DURATION_RANGES');
    }
    if (errors.noIntersections) {
      return this.translate.instant('MEETING_SCHEDULER.SCHEDULE_SETTINGS.AVAILABILITY.EDIT_NO_INTERSECTIONS');
    }
    if (errors.noDuplicates) {
      return this.translate.instant('MEETING_SCHEDULER.SCHEDULE_SETTINGS.AVAILABILITY.EDIT_NO_DUPLICATES');
    }
    return this.translate.instant('MEETING_SCHEDULER.SCHEDULE_SETTINGS.AVAILABILITY.EDIT_UNKNOWN_ERROR');
  }

  isInvalid(): boolean {
    return !this.form.valid;
  }

  timeToMoment(time: string): Moment {
    return moment(time, 'HH:mm');
  }

  dateToTime(date: Date): string {
    return moment(date).format('HH:mm');
  }

  timePickerHasErrors(side: 'start' | 'end', index: number, hasErrors: boolean): void {
    const errors = side === 'start' ? this.startsErrors.controls : this.endsErrors.controls;
    if (errors[index].value !== hasErrors) {
      errors[index].setValue(hasErrors);
      this.cd.detectChanges();
    }
  }
}
